import { PropsWithChildren, useEffect, useRef, useState } from 'react'
import { UserContext } from './UserContext'
import { useAuthState, useClient } from '@based/sdk/react'
import { User } from '@schema/user'

const VISIT_THROTTLE_MS = 60_000

export const UserProvider = ({ children }: PropsWithChildren) => {
  const client = useClient()
  const authState = useAuthState()
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const isAuthed = !!(authState && authState.userId)
  const lastVisitCallAt = useRef<number>(0)

  useEffect(() => {
    if (isAuthed) {
      setIsLoading(true)
      client
        .query('current-user', { userId: authState.userId })
        .get()
        .then((result) => {
          setUser(result)
          setIsLoading(false)
        })
        .catch((error) => {
          console.warn('Failed to fetch user data:', error)
          setUser(null)
          setIsLoading(false)
        })
    } else {
      setUser(null)
      setIsLoading(false)
    }
  }, [client, isAuthed, authState?.userId])

  useEffect(() => {
    lastVisitCallAt.current = 0

    if (!isAuthed) return

    const registerVisit = () => {
      const now = Date.now()
      if (now - lastVisitCallAt.current < VISIT_THROTTLE_MS) return
      lastVisitCallAt.current = now
      client.call('register-visit').catch((error) => {
        console.warn('Failed to register visit:', error)
      })
    }

    registerVisit()

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        registerVisit()
      }
    }

    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [client, isAuthed, authState?.userId])

  return (
    <UserContext.Provider
      value={{ user, authState, isAuthenticated: isAuthed, isLoading }}
    >
      {children}
    </UserContext.Provider>
  )
}
